import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PlusCircle, Swords } from "lucide-react";
import WalletPanel from "@/components/dashboard/WalletPanel";
import ConnectPanel from "@/components/dashboard/ConnectPanel";
import AdminPanel from "@/components/dashboard/AdminPanel";
import UploadModal from "@/components/dashboard/UploadModal";
import CharacterModal from "@/components/dashboard/CharacterModal";
import { AnalyticsPanel, EmailPrefsPanel } from "@/components/dashboard/AnalyticsAndPrefs";
import { StatCard, ReactionsInbox, WorksList, PurchasesList, FollowingList } from "@/components/dashboard/Sections";

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [myWorks, setMyWorks] = useState([]);
  const [myCharacters, setMyCharacters] = useState([]);
  const [charPurchases, setCharPurchases] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [follows, setFollows] = useState([]);
  const [followerCount, setFollowerCount] = useState(null);
  const [connect, setConnect] = useState(null);
  const [flagged, setFlagged] = useState(null);
  const [vendorStatus, setVendorStatus] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [characterOpen, setCharacterOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const isAdmin = user && user !== false && user.email === "admin@quill.ink";

  useEffect(() => {
    if (!loading && (!user || user === false)) nav("/auth");
  }, [user, loading, nav]);

  const load = async () => {
    try {
      const [w, m, p, r, c, f, fc, ch, chp, cr] = await Promise.all([
        api.get("/wallet"),
        api.get("/works/mine/list"),
        api.get("/purchases/mine"),
        api.get("/reactions/mine"),
        api.get("/connect/status"),
        api.get("/follows/mine"),
        api.get("/follows/followers/count"),
        api.get("/characters/mine/list"),
        api.get("/characters/mine/purchased"),
        api.get("/character-reactions/mine"),
      ]);
      setWallet(w.data);
      setMyWorks(m.data);
      setPurchases(p.data);
      // Combine work + character reactions into one inbox
      const combined = [
        ...r.data.map((x) => ({ ...x, kind: "work" })),
        ...cr.data.map((x) => ({ reaction: x.reaction, work_title: x.character_name, kind: "character" })),
      ];
      combined.sort((a, b) => new Date(b.reaction.created_at) - new Date(a.reaction.created_at));
      setReactions(combined);
      setConnect(c.data);
      setFollows(f.data);
      setFollowerCount(fc.data.count);
      setMyCharacters(ch.data);
      setCharPurchases(chp.data);
      if (user && user !== false && user.email === "admin@quill.ink") {
        try {
          const [{ data }, vs] = await Promise.all([
            api.get("/admin/flagged"),
            api.get("/admin/vendor-scan-status"),
          ]);
          setFlagged(data);
          setVendorStatus(vs.data);
        } catch (err) {
          console.error("Admin panel load failed", err);
        }
      }
    } catch (err) {
      console.error("Dashboard load failed", err);
    }
  };

  const adminAction = async (workId, action) => {
    if (action === "hard_delete" && !window.confirm("Hard-delete this work? Its cover hash will be blocklisted and the file removed.")) return;
    try {
      await api.post(`/admin/works/${workId}/action`, { action });
      toast.success(`Action '${action}' applied.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/digest/send-now");
      if (data.sent) toast.success(`Digest sent to ${data.recipient} · ${data.flagged} flagged, ${data.suspended} suspended, ${data.incidents} incidents.`);
      else toast.error(`Digest failed: ${data.reason}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingDigest(false);
    }
  };

  const exportNcmec = async () => {
    try {
      const token = localStorage.getItem("quill_token");
      const resp = await fetch(`${API}/admin/ncmec/export.csv`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ncmec_incidents_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("NCMEC CSV export failed", err);
      toast.error("CSV export failed.");
    }
  };

  useEffect(() => { if (user && user !== false) load(); }, [user]);

  useEffect(() => {
    if (params.get("connect") === "done") {
      toast.success("Payout account updated.");
      nav("/dashboard", { replace: true });
    }
  }, [params, nav]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      const status = e?.response?.status;
      const detail = formatApiError(e);
      if (status === 503) {
        toast.error("Stripe Connect not enabled yet — see the panel below for the one-click activation link.", { duration: 8000 });
      } else {
        toast.error(detail);
      }
      setConnecting(false);
    }
  };

  const withdraw = async () => {
    if (!wallet || wallet.wallet_cents < 100) { toast.error("Minimum withdrawal is $1.00"); return; }
    setWithdrawing(true);
    try {
      const { data } = await api.post("/wallet/withdraw", { amount_cents: wallet.wallet_cents });
      toast.success(data.kind === "stripe_connect" ? "Sent to your bank via Stripe." : "Withdrawal recorded (simulated). Connect Stripe for real payouts.");
      await load();
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading || !user || user === false) return <div className="min-h-screen grain"><Nav /></div>;

  const balance = ((wallet?.wallet_cents || 0) / 100).toFixed(2);
  const totalWorks = myWorks.length;
  const totalSales = myWorks.reduce((s, w) => s + (w.total_sales_cents || 0), 0);
  const totalTips = myWorks.reduce((s, w) => s + (w.total_tips_cents || 0), 0);

  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="chip mb-3">Author Study</div>
            <h1 className="font-serif-display text-3xl sm:text-5xl text-[#F7F3EC] tracking-tight">
              Welcome, <span className="italic text-[#E6B860]">{user.pseudonym}</span>
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCharacterOpen(true)}
              className="btn-ghost-gold px-5 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest inline-flex items-center gap-2"
              data-testid="new-character-button"
            >
              <Swords className="w-4 h-4" /> New character
            </button>
            <button
              onClick={() => setUploadOpen(true)}
              className="btn-buy px-5 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2"
              data-testid="upload-work-button"
            >
              <PlusCircle className="w-4 h-4" /> Sell new fragment
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-14">
          <StatCard label="Wallet balance" value={`$${balance}`} sub="Available payout" testid="stat-balance" />
          <StatCard label="Fragments" value={totalWorks} sub="Listed for sale" testid="stat-works" />
          <StatCard label="Sales" value={`$${(totalSales / 100).toFixed(2)}`} sub="Lifetime" testid="stat-sales" />
          <StatCard label="Tips" value={`$${(totalTips / 100).toFixed(2)}`} sub="Anonymous" testid="stat-tips" />
        </div>

        <WalletPanel wallet={wallet} balance={balance} withdrawing={withdrawing} onWithdraw={withdraw} />
        <AnalyticsPanel />
        <ConnectPanel connect={connect} connecting={connecting} onStart={startConnect} />

        {isAdmin && (
          <AdminPanel
            flagged={flagged}
            vendorStatus={vendorStatus}
            sendingDigest={sendingDigest}
            onAction={adminAction}
            onExportNcmec={exportNcmec}
            onSendDigest={sendDigest}
          />
        )}

        <FollowingList follows={follows} followerCount={followerCount} />
        <EmailPrefsPanel />
        <ReactionsInbox reactions={reactions} />
        <WorksList myWorks={myWorks} />

        {/* Characters */}
        <section className="mb-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC] flex items-center gap-2">
              <Swords className="w-5 h-5 text-[#D9B472]" /> Your characters
            </h2>
            <Link to="/vault" className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass hover:text-[#E6B860]">Visit the Vault →</Link>
          </div>
          {myCharacters.length === 0 ? (
            <p className="text-[#6E675E] font-serif-body italic">No characters listed yet. Send a forgotten hero or villain into the vault.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {myCharacters.map((c) => (
                <Link key={c.id} to={`/character/${c.id}`} className="gold-frame paper-scroll rounded-sm p-4" data-testid={`my-character-${c.id}`}>
                  <div className="chip mb-2">{c.character_type}</div>
                  <div className="font-serif-display italic text-lg text-[#F7F3EC] line-clamp-1">{c.name}</div>
                  <div className="text-xs text-[#A8A095] font-serif-body italic line-clamp-2 mb-2">{c.hook}</div>
                  <div className="flex justify-between items-center text-[10px] font-mono-brass uppercase tracking-widest pt-2 border-t border-[rgba(217,180,114,0.1)]">
                    <span className="text-[#D9B472]">${((c.price_cents || 0) / 100).toFixed(2)}</span>
                    <span className="text-[#6E675E]">{c.moderation_status}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <PurchasesList purchases={purchases} />

        {charPurchases.length > 0 && (
          <section className="mb-14">
            <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC] mb-6 flex items-center gap-2">
              <Swords className="w-5 h-5 text-[#D9B472]" /> Characters you've unlocked
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {charPurchases.map(({ purchase, character }) => (
                <Link key={character.id} to={`/character/${character.id}`} className="gold-frame paper-scroll rounded-sm p-4" data-testid={`purchased-character-${character.id}`}>
                  <div className="chip mb-2">{character.character_type}</div>
                  <div className="font-serif-display italic text-lg text-[#F7F3EC] line-clamp-1">{character.name}</div>
                  <div className="text-xs text-[#A8A095] italic mb-2">by {character.author_pseudonym}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass pt-2 border-t border-[rgba(217,180,114,0.1)]">Unlocked · {new Date(purchase.purchased_at).toLocaleDateString()}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} onCreated={() => { setUploadOpen(false); load(); }} />}
      {characterOpen && <CharacterModal onClose={() => setCharacterOpen(false)} onCreated={() => { setCharacterOpen(false); load(); }} />}
    </div>
  );
}
